<template>
    <div>
        <div v-if="active" class="event-tab">
            <div class="info wrapper dark fs-small bg-lightest px-4">
                <div class="text-center mb-2" v-if="eventWarning">
                    <div class="w-100 radius-normal lightest text-nowrap" :class="['bg-' + eventWarning.bgColor]">
                        {{ eventWarning.message }}
                    </div>
                    <span v-if="cancellationReason" class="color-6">{{ cancellationReason }}</span>
                </div>

                <div class="d-flex">
                    <div class="detailLabel bold">
                        Departure:
                    </div>

                    <div class="darker bold p-0">
                        {{ formatDateDots(event.start) + ' ' + formatTime(event.start) }}
                    </div>
                </div>

                <div class="d-flex mt-2">
                    <div class="detailLabel bold">
                        Duration:
                    </div>

                    <div class="darker bold p-0">
                        {{ hourToDuration(this.event.duration) }}
                    </div>
                </div>

                <div class="d-flex mt-2"
                    v-if="event.product && event.product.transport && event.product.transport.length > 0">
                    <div class="detailLabel bold">
                        Transport:
                    </div>

                    <div class="darker bold p-0">
                        {{ event.product.transport.name }}
                    </div>
                </div>

                <div class="d-flex mt-2" v-if="event.location">
                    <div class="detailLabel bold">
                        Location:
                    </div>

                    <div class="darker bold p-0">
                        {{ event.location }}
                    </div>
                </div>

                <div class="d-flex mt-2">
                    <div class="detailLabel bold">
                        Channel:
                    </div>

                    <div class="darker bold p-0 d-flex flex-row">
                        <div class="d-flex align-items-center" v-tooltip="channelToolTips.agent">
                            <span class="channelCircle mr-1 radius-circle bg-medium"
                                :class="{ 'bg-color-4': salesChannelsStatus.agentCustom, 'bg-color-3': salesChannelsStatus.agent, 'bg-dark': !salesChannelsStatus.agent }"></span>
                            <span>Agents</span>
                        </div>

                        <div class="d-flex align-items-center ml-2" v-tooltip="channelToolTips.widget">
                            <span class="channelCircle mr-1 radius-circle bg-medium"
                                :class="{ 'bg-color-3': salesChannelsStatus.widget, 'bg-dark': !salesChannelsStatus.widget }"></span>
                            <span>Widget</span>
                        </div>

                        <div class="d-flex align-items-center ml-2" v-tooltip="channelToolTips.me">
                            <span class="channelCircle mr-1 radius-circle bg-medium"
                                :class="{ 'bg-color-3': salesChannelsStatus.me, 'bg-dark': !salesChannelsStatus.me }"></span>
                            <span>Me</span>
                        </div>
                    </div>
                </div>

                <div class="d-flex mt-2">
                    <div class="detailLabel bold">
                        Cutoff:
                    </div>

                    <div class="darker bold p-0" v-tooltip="cutoffDescriptionTooltip">
                        {{ cutoffDescription }}
                    </div>
                </div>

                <div class="d-flex mt-2" v-if="event.hasCutOff">
                    <div class="detailLabel bold">
                        Soft Cutoff:
                    </div>

                    <div class="darker bold p-0" v-tooltip="cutoffType.description">
                        {{ cutoffType.label }}
                    </div>
                </div>

                <div class="d-flex mt-2">
                    <div class="detailLabel bold">
                        Rules:
                    </div>

                    <div class="darker bold p-0">
                        {{ rulesDescription ? 'Yes' : 'No' }}
                    </div>
                </div>
            </div>

            <div class="wrapper d-flex px-4 pb-0">
                <!-- Prices Card -->
                <div class="w-100 px-3 bg-lighter radius-normal border-medium py-2 mr-0 d-flex flex-column">
                    <div class="border-bottom-light d-flex justify-content-between align-items-center pb-2 mb-2">
                        <div class="text-uppercase fs-smaller darker bold mt-1">Prices</div>
                        <div>
                            <v-select v-if="getChannelsWithPrices.length > 0 && !isGenderPriceLoading"
                                class="compact-dropdown" :options="getChannelsWithPrices"
                                :disabled="getChannelsWithPrices.length === 0" label="channelName"
                                track-by="channelName" :value="pricesChannel" @input="updatePricesChannel($event)">
                            </v-select>
                        </div>
                    </div>

                    <div class="d-flex justify-content-start align-items-center pb-2">
                        <span class="fs-smaller bold darker">{{ getPriceType }}</span>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pb-2 my-1"
                        v-if="event.passengerSettings && event.passengerSettings.format === 2">
                        <div class="fs-smaller dark bold mt-1">Categories</div>
                        <div>
                            <v-select v-if="true" class="compact-dropdown" :options="getEventGenderPrices"
                                :disabled="false" label="audienceTypeDescription" track-by="audienceTypeDescription"
                                :value="categorySelection" @input="updateCategory($event)">
                            </v-select>
                        </div>
                    </div>

                    <!-- Gender Prices - Price per Order | Price per Person -->
                    <div v-if="event.eventGenderPrices[0].genderPriceType !== 3">
                        <div v-for="(price, index) in getDisplayedPrices(pricesChannel)" :key="index">
                            <div v-if="!(price.genderPriceType == 1 && index > 0) && !isGenderPriceLoading && !price.error"
                                class="prices-grid justify-content-between fs-smaller">
                                <div class="dark elipsis mr-2 cursor-default text-uppercase bold">
                                    {{ genderPriceName(price) }}
                                </div>
                                <div class="d-flex justify-content-end">
                                    <span class="darker bold text-nowrap">{{ formatPrice(price.finalPrice) }} €</span>
                                </div>
                            </div>

                            <div v-if="price.error">
                                <div class="d-flex justify-content-center align-items-center my-3">
                                    <span class="darker bold text-nowrap">No information found.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else>
                        <!-- Gender Prices - Per Group | Price per Person & Price per Group -->
                        <div v-if="event.passengerSettings && event.passengerSettings.format !== 2">
                            <div class="prices-grid justify-content-between fs-smaller"
                                v-for="price, index in pricesChannel.prices" :key="index">
                                <div v-if="price.upperBound" class="dark mr-2 cursor-default text-uppercase bold">
                                    {{ `${price.lowerBound} to ${price.upperBound}` }}</div>

                                <div v-else
                                    class="dark mr-2 cursor-default text-uppercase bold d-flex align-items-start justify-content-start">
                                    <span class="mr-1">{{ `${price.lowerBound} to` }}</span>

                                    <span v-if="event.availabilityType === 1"
                                        class="d-flex align-items-center fs-large infinity-height">&#8734;</span>
                                    <span v-else class="mr-1">{{ getMaxSeats }}</span>
                                </div>

                                <div class="d-flex justify-content-end">
                                    <span class="darker bold text-nowrap">{{ formatPrice(price.finalPrice) }} €</span>
                                </div>
                            </div>
                        </div>

                        <!-- Gender Prices - Per Group | Price per Category Tier -->
                        <div
                            v-else-if="categorySelection && categorySelection.hashedAudience && event.passengerSettings && event.passengerSettings.format === 2">
                            <div v-for="price, index in pricesChannel.prices[categorySelection.hashedAudience]"
                                :key="index" class="prices-grid justify-content-between fs-smaller">

                                <div v-if="price.upperBound" class="dark mr-2 cursor-default text-uppercase bold">
                                    {{ `${price.lowerBound} to ${price.upperBound}` }}</div>

                                <div v-else
                                    class="dark mr-2 cursor-default text-uppercase bold d-flex align-items-start justify-content-start">
                                    <span class="mr-1">{{ `${price.lowerBound} to` }}</span>
                                    <span v-if="event.availabilityType === 1"
                                        class="d-flex align-items-center fs-large infinity-height">&#8734;</span>
                                    <span v-else class="mr-1">{{ getMaxSeats }}</span>
                                </div>

                                <div class="d-flex justify-content-end">
                                    <span class="darker bold text-nowrap">{{ formatPrice(price.price) }} €</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="isGenderPriceLoading"
                        class="d-flex flex-column justify-content-center align-items-center my-2">
                        <div class="fs-small mb-1">Loading Prices</div>
                        <loader size="small"></loader>
                    </div>
                </div>
            </div>
        </div>

        <div class="display-grid wrapper px-4">
            <!-- Available Card -->
            <div class="w-100 px-3 bg-lighter radius-normal border-medium py-2 mr-1 d-flex flex-column">
                <div class="pt-1">
                    <div class="border-bottom-light">
                        <div class="text-uppercase fs-smallest dark">Available</div>

                        <div v-if="event.availabilityType === 1" class="fs-giant" v-tooltip="'Unlimited'">
                            <i class="fas fa-infinity"></i>
                        </div>
                        <div v-else class="fs-giant">{{ event.quantityAvailable }}</div>
                        <div class=" mb-4">
                            <span v-if="totalReserved"
                                class="lightest bg-color-1 radius-normal fs-smaller px-1 bold">{{ totalReserved }} booking
                                now</span>

                            <!-- empty space to equal the spacing -->
                            <span v-else>&nbsp;</span>
                        </div>
                    </div>
                </div>

                <div class="border-bottom-light py-1" v-if="event.availabilityType === 0">
                    <div class="text-uppercase fs-smallest dark bold" v-tooltip="'Total Capacity'">Total Capacity</div>
                    <div class="bold">
                        <span>{{ event.maximumLotation }}</span>
                    </div>
                </div>

                <div class="border-bottom-light">
                    <div v-if="event.eventResources.length < 1"
                        class="light h-100 d-flex align-items-center justify-content-center fs-small py-1">No resources
                    </div>
                    <div class="pb-1" v-else>
                        <div class="justify-content-between fs-smaller" v-for="resource in event.eventResources"
                            :key="resource.id">
                            <div class="dark mr-2 cursor-default text-uppercase bold mt-1"
                                v-tooltip="`${resource.name} ${(event.availabilityType == 2) ? resource.capacityAvailable + '/' + resource.maxSeats : ''}`">
                                {{ resource.name }}</div>
                            <div v-if="event.availabilityType == 2" class="d-flex">
                                <span class="darker bold">{{ resource.capacityAvailable }}</span>
                                <span class="dark bold">/{{ resource.maxSeats }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="border-bottom-light py-1">
                    <div class="d-flex justify-content-between align-items-baseline">
                        <div class="text-uppercase fs-smaller dark bold" v-tooltip="'Occupancy'">
                            Occup...
                        </div>

                        <div class="bold">
                            <span v-html="occupancy"></span>
                        </div>
                    </div>
                </div>

                <div class="py-1">
                    <div class="d-flex justify-content-between align-items-baseline">
                        <div class="text-uppercase fs-smaller dark bold">
                            Booked
                        </div>

                        <div class="bold">
                            {{ event.quantityBooked }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Check-in Card -->
            <div class="w-100 px-3 bg-lighter radius-normal border-medium pt-2 ml-1 d-flex flex-column">
                <div class="py-1">
                    <div class="text-uppercase fs-smallest dark">Check-ins</div>
                    <div class="fs-giant">{{ event.totalPAXCheckedIn }}/{{ event.totalPAXCheckedIn + event.totalPAXMissing }}</div>
                </div>
                <div class="border-bottom-light pb-1" >
                    <div class="checkin-grid fs-smaller">
                        <div class="text-uppercase dark bold">
                            No-Shows
                        </div>
                        <div class="bold text-right">
                            {{ event.totalPAXNoShow }}
                        </div>
                        <div class="text-uppercase dark bold color-6">
                            Missing
                        </div>
                        <div class="bold color-6 text-right">
                            {{ event.totalPAXMissing }}
                        </div>
                    </div>
                </div>
                <div class="mb-2 flexGrow1 py-1">
                    <div class="text-uppercase fs-smallest dark pb-2" v-if="orderedEventOccupancyList.length > 0">Breakdown</div>
                    <div v-for="(clientType, index) in orderedEventOccupancyList" :key="index">
                        <div class="d-flex justify-content-between" v-if="isToShowThisClientType(clientType)">
                            <div class="dark elipsis flexGrow1 mr-2 text-uppercase bold fs-smaller"
                                v-tooltip="genderPriceName2(clientType)">{{ genderPriceName2(clientType, 'Order') }}
                            </div>

                            <div class="fs-smaller">
                                <span class="darker bold">{{ clientType.quantity }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { eventMixin } from '@/mixins/event'
import { reservationsMixin } from '@/mixins/reservations'

import Notes from './EventNotes'
import EventMeter from './EventMeter'
import extendedVSelect from '@/extends/vue-select'
import _ from 'lodash'
import Loader from '@/tools/loaders/loader1'

export default {
    data() {
        return {
            pricesChannel: {
                channelName: '',
                channelId: null
            },
            genderPriceSelected: [],
            categorySelection: null,
        }
    },
    props: {
        active: Boolean,
        event: Object
    },
    watch: {
        getGenderPricePerChannel: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.channels && newVal.channels.length > 0) {
                    this.initGenderPrices();
                }
            }
        }
    },
    computed: {
        ...mapGetters([
            'account',
            'getEventColors',
            'getSalesChannels',
            'getGenderPricePerChannel',
            'getLoadingGenderPrice',
        ]),
        getEventGenderPrices() {
            const categories = this.event.eventGenderPrices.map((gp) => {
                return {
                    audienceType: gp.audienceType,
                    audienceTypeDescription: gp.audienceTypeDescription,
                    hashedAudience: gp.hashedAudience
                }
            });

            this.categorySelection = categories[0];

            return categories;
        },
        getMaxSeats() {
            const lotations = {
                0: this.event.maximumLotation,
                2: this.event.eventResources.reduce((acc, cur) => acc += cur.maxSeats, 0)
            }

            return lotations[this.event.availabilityType];
        },
        getPriceType() {
            const priceTypes = {
                1: 'Per Order',
                2: 'Per Person',
                3: 'Per Group',
            };

            const { genderPriceType } = this.event.eventGenderPrices[0];

            if (genderPriceType === 3) {
                const { format } = this.event.passengerSettings;
                const baseLabel = priceTypes[genderPriceType];
                const extraLabel = {
                    0: '(Price per Group)',
                    1: '(Price per Person)',
                    2: '(Price per Category Tier)'
                }[format]

                return `${baseLabel} ${extraLabel}`;
            }

            return priceTypes[genderPriceType];
        },
        getChannelsWithPrices() {
            const data = this.getGenderPricePerChannel || {};
            const channels = Array.isArray(data.channels) ? data.channels : [];
            const pricingCategories = Array.isArray(data.pricingCategories) ? data.pricingCategories : [];

            if (channels.length === 0 || pricingCategories.length === 0) {
                return [];
            }

            return this.getGenderPricePerChannel.channels.reduce((acc, cur) => {
                acc.push({
                    channelId: cur.channelId,
                    channelName: cur.channelName,
                    prices: this.getFinalPrices(cur),
                    pricingCategories: this.getGenderPricePerChannel.pricingCategories.map(categorie => categorie.audienceTypeDescription)
                });

                return acc;
            }, []);
        },
        isGenderPriceLoading() {
            return this.getLoadingGenderPrice;
        },
        rulesDescription() {
            if (!this.event.eventPriceGroups) return false;

            return this.event.eventPriceGroups.length > 0 ? true : false;
        },
        orderedEventOccupancyList() {
            return _.orderBy(this.event.eventOccupancyList, ['audienceTypeDescription'], ['asc']);
        },
        eventWarning() {
            if (this.event.status == -1) {
                return {
                    message: 'Event Canceled',
                    tooltip: this.event.statusMessage,
                    bgColor: 'color-6'
                }
            } else if (this.isEventInsideCutoff(this.event)) {
                return {
                    message: this.event.softCutOff == true ? 'One or more channels are in Soft Cutoff' : 'One or more channels are in Cutoff',
                    tooltip: this.getSalesChannels ? this.event.cutOffChannelsMain.map(cutoffChannel => this.getSalesChannels.length > 0 && this.getSalesChannels.find(salesChannel => salesChannel.id == cutoffChannel.channelId).brandName).join("<br>") : null,
                    bgColor: this.event.softCutOff == true ? 'color-5' : 'color-6'
                }
            } else {
                return false;
            }
        },
        isPerOrderWithParticipants() {
            let genderPricesExcludedFromBilling = this.event.eventGenderPrices.find(i => i.isExcludedFromBilling)
            if (genderPricesExcludedFromBilling) {
                return this.event.eventGenderPrices.length > 1 && genderPricesExcludedFromBilling.genderPriceType == 1;
            } else {
                return false;
            }
        },
        eventColor() {
            let vm = this
            return this.getEventColors.filter(function (storedColor) {
                return vm.event.product.color == storedColor.id;
            })[0]
        },
        totalReserved() {
            return this.event.quantityReserved ? this.event.quantityReserved : 0;
        },
        occupancy() {
            const isUnlimited = !this.event.hasLotation;
            const occupancy = this.event.maximumLotation == 0 ? "&ndash;" : `${Math.round((this.event.quantityBooked * 100) / this.event.maximumLotation)}&#37;`;

            if (isUnlimited) {
                return '<span title="This event has unlimited capacity.">&#8734;</span>';
            } else {
                return `${occupancy}`;
            }
        },
        multipleDays() {
            return (this.formatDate(this.event.start) != this.formatDate(this.event.end));
        },
        resourcesString() {
            if (this.event.product.resources) {
                return (this.event.product.resources.length > 1) ? 'Resources' : 'Resource';
            } else {
                return 'Resource';
            }
        },
        calcQuantityAvailable() {
            return this.event.maximumLotation - this.event.quantityBooked;
        },
        reservationHasPricePerPerson() {
            return this.event.eventOccupancyList.length > 0 && this.event.eventOccupancyList[0].productPriceType == 2;
        },
        salesChannelsStatus() {
            return {
                me: this.canISellThisEvent(this.event).allowed,
                widget: this.canWidgetSellThisEvent(this.event).allowed,
                agent: this.canAgentSellThisEvent(this.event).allowed,
                agentCustom: this.canAgentCustomSellThisEvent(this.event),
            }
        },
        channelToolTips() {
            let textWidget = 'Channel ' + (this.salesChannelsStatus.widget ? 'Open' : 'Closed<br>' + this.canWidgetSellThisEvent(this.event).reasons.join(", ") + '.');
            let textMe = 'Channel "' + this.account.salesChannel.name + '" is ' + (this.salesChannelsStatus.me ? 'Open' : 'Closed<br>' + this.canISellThisEvent(this.event).reasons.join(", ") + '.');
            let textAgent = 'Channel ';

            if (this.salesChannelsStatus.agent) {
                if (this.salesChannelsStatus.agentCustom) {
                    textAgent += 'Open for some agents';
                } else {
                    textAgent += 'Open';
                }
            } else {
                textAgent += 'Closed<br>' + this.canAgentSellThisEvent(this.event).reasons.join(", ") + '.';
            }

            return {
                me: textMe,
                widget: textWidget,
                agent: textAgent
            }
        },
        cutoffDescription() {
            if (!this.event.hasCutOff || this.event.hasCutOff != true) {
                return 'No';
            } else {
                let output = 'Yes, '
                output += (moment().isBefore(moment(this.event.eventCutOffStart))) ? 'starts' : 'started';
                output += ' ' + moment().to(moment(this.event.eventCutOffStart)); //"7 hours ago"

                return output;
            }
        },
        cutoffDescriptionTooltip() {
            if (!this.event.hasCutOff || this.event.hasCutOff != true) {
                return null;
            } else {
                return this.formatDateDots(this.event.eventCutOffStart) + ' ' + this.formatTime(this.event.eventCutOffStart);
            }
        },
        cutoffType() {
            if (this.event.softCutOff == true) {
                return {
                    label: 'Yes',
                    description: 'Reservations will be pending'
                }
            } else {
                return {
                    label: 'No',
                    description: 'No reservations allowed'
                }
            }
        },
        cancellationReason() {
            if (this.event.status === -1 && !!this.event.statusMessage) return this.event.statusMessage;

            return null;
        },
        formattedCancellationDate() {
            if (this.event && this.event.cancelationDate) return moment(this.event.cancelationDate).format('DD.MM.YYYY HH:mm');

            return null;
        },
    },
    methods: {
        initGenderPrices() {
            const { id: salesChannelId } = this.account.salesChannel;
            const genderPriceChannels = this.getChannelsWithPrices;

            // Find user default channel
            const selectedChannel = genderPriceChannels.find(channel => channel.channelId === salesChannelId) || genderPriceChannels[0] || {};

            this.pricesChannel = selectedChannel;
        },
        getFinalPrices(cur) {
            if (!this.getGenderPricePerChannel) return [];

            const format = this.event.passengerSettings && this.event.passengerSettings.format;
            let finalPrices = [];

            if (this.event.eventGenderPrices[0].genderPriceType === 3) {
                if (!this.getGenderPricePerChannel.passengerSettings) return [];

                if (format !== 2) {
                    finalPrices = this.getGenderPricePerChannel.passengerSettings.configurations.map(cur1 => ({
                        lowerBound: cur1.lowerBound,
                        upperBound: cur1.upperBound,
                        finalPrice: this.roundValue(Number((cur1.price * cur.multiplier).toFixed(2)), this.getGenderPricePerChannel.roundType)
                    }));
                } else {
                    finalPrices = this.event.eventGenderPrices.reduce((acc, gp) => {
                        const tiers = this.getGenderPricePerChannel.passengerSettings.tieredConfigurations[gp.hashedAudience] || [];

                        acc[gp.hashedAudience] = tiers.map(tier => ({
                            ...tier,
                            price: this.roundValue(Number((tier.price * cur.multiplier).toFixed(2)), this.getGenderPricePerChannel.roundType)
                        }));

                        return acc;
                    }, {});
                }
            } else {
                let genderPriceType = null;

                if (this.event && this.event.eventGenderPrices && this.event.eventGenderPrices.length > 0) {
                    genderPriceType = this.event.eventGenderPrices[0].genderPriceType;
                }

                finalPrices = this.getGenderPricePerChannel.pricingCategories.map(cur1 => {
                    const eventGenderPrice = this.event.eventGenderPrices.find(gp => gp.audienceTypeDescription === cur1.audienceTypeDescription);
                    return {
                        audienceTypeDescription: cur1.audienceTypeDescription,
                        audienceTypeLabel: eventGenderPrice ? eventGenderPrice.audienceTypeLabel : null,
                        genderPriceType: genderPriceType,
                        finalPrice: this.roundValue(Number((cur1.grossPrice * cur.multiplier).toFixed(2)), this.getGenderPricePerChannel.roundType)
                    };
                });
            }

            return finalPrices;
        },
        getDisplayedPrices(channel) {
            if (!channel || !channel.prices || !this.getGenderPricePerChannel) return [];

            const genderPriceType = this.event && this.event.eventGenderPrices && this.event.eventGenderPrices[0]
                ? this.event.eventGenderPrices[0].genderPriceType
                : null;

            // Only apply special logic when per order
            if (genderPriceType === 1) {
                const categories = Array.isArray(this.getGenderPricePerChannel.pricingCategories) ? this.getGenderPricePerChannel.pricingCategories : [];

                // Find category with empty description and grossPrice > 0 | always present when per order
                const isFoundPrice = categories.find(cat => (cat.audienceTypeDescription === '' || cat.audienceTypeDescription === null) && Number(cat.grossPrice) >= 0);

                if (isFoundPrice) {
                    const match = channel.prices.find(prices => prices.audienceTypeDescription === isFoundPrice.audienceTypeDescription && prices.finalPrice === isFoundPrice.grossPrice);
                    if (match) return [match];
                } else {
                    return [{
                        error: true
                    }]
                }
            }

            // Default: return all prices
            return channel.prices || [];
        },
        roundValue(value, roundRuleType) {
            switch (roundRuleType) {
                case 'NearestWholeNumber':
                    return Math.round(value);
                case 'OneDecimalPlace':
                    return Math.round(value * 10) / 10;
                default:
                    return value;
            }
        },
        genderPriceName2(clientType, alternative) {
            if (clientType.audienceType) {
                return clientType.audienceTypeLabel ? clientType.audienceTypeDescription + ' - ' + clientType.audienceTypeLabel : clientType.audienceTypeDescription
            } else {
                return alternative
            }
        },
        genderPriceName(genderPrice) {
            const priceTypesToValidate = [2, 3];

            if (priceTypesToValidate.includes(genderPrice.genderPriceType)) {
                return this.genderPriceObj(genderPrice, true).description ? this.genderPriceObj(genderPrice, true).name + ' - ' + this.genderPriceObj(genderPrice, true).description : this.genderPriceObj(genderPrice, true).name;
            } else {
                return 'Ticket';
            }
        },
        isToShowThisClientType(clientType) {
            if (this.isPerOrderWithParticipants && clientType.audienceType === null) {
                return false;
            } else {
                return true;
            }
        },
        updatePricesChannel(event) {
            const selectedChannel = this.getGenderPricePerChannel.channels.find(channel => channel.channelId === event.channelId);

            this.pricesChannel = event;
        },
        updateCategory(event) {
            this.categorySelection = event;
        }
    },
    components: {
        EventMeter,
        Notes,
        vSelect: extendedVSelect,
        Loader,
    },
    mixins: [
        eventMixin,
        reservationsMixin
    ]
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/_variables.scss';

.display-grid {
    display: grid;
    grid-template-columns: 47% 51%;
    grid-template-rows: 1fr;
    grid-template-areas: ". .";
}

.prices-grid {
    display: grid;
    grid-template-columns: 80% 20%;
    grid-template-rows: 1fr;
    gap: 0px 0%;
    grid-template-areas: ". .";
}

.elipsis {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.detailLabel {
    width: 80px;
}

.infinity-height {
    height: 17px;
}
</style>
<style lang="scss">
.checkin-grid {
    display: grid;
    grid-template-columns: 1fr auto;
    row-gap: 0;
    line-height: 1.6;
}
.compact-dropdown {
    .dropdown-toggle {
        height: 1.5rem !important;
        width: 150px !important;

        .vs__actions {
            top: 3px !important;
        }

        .selected-tag,
        & input[type=search] {
            height: 1.4rem !important;
            margin: 0 !important;
            line-height: 1.4rem !important;
            font-size: 11px !important;
        }
    }

    .dropdown-menu {
        font-size: 11px !important;

        li[role=option] a {
            padding-left: 15px !important;
        }
    }

    .open-indicator {
        height: 14px !important;
    }
}
</style>


// WEBPACK FOOTER //
// EventDetails.vue?b5974bc4